import { defineStore } from 'pinia'
import { hexToRGB } from '@/utils/colors'
import type { ClientState, LegalTexts, LegalContentType } from '@/models/Client.model'
import type { TransactionMetaDataResponse } from '@/models/Transaction.model'

export const useClientStore = defineStore('client', {
  state: (): ClientState => ({
    loading: false,
    error: null,
    clientUuid: null,
    logo: '',
    currency: 'EUR',
    colors: {
      primary: null,
      secondary: null,
      primaryRGB: null,
      secondaryRGB: null,
    },
    legal: {
      terms: '',
      privacyPolicy: '',
      imprint: '',
      publicInfo: '',
      modalContent: null,
      modalIsVisible: false,
    },
  }),

  actions: {
    setColors(colors: { primary?: string, secondary?: string }) {
      if (colors.primary && colors.primary.length > 0) {
        this.colors.primary = colors.primary
        this.colors.primaryRGB = hexToRGB(colors.primary)
      }
      if (colors?.secondary && colors.secondary.length > 0) {
        this.colors.secondary = colors.secondary
        this.colors.secondaryRGB = hexToRGB(colors.secondary)
      }
    },

    setCurrency(currency?: string) {
      if (currency) {
        this.currency = currency
      }
    },

    setLogo(logo?: string) {
      if (logo) {
        this.logo = logo
      }
    },

    setClientUuid(clientUuid?: string) {
      if (clientUuid) {
        this.clientUuid = clientUuid
      }
    },

    setContent(content: Partial<ClientState>) {
      Object.assign(this, content)
    },

    setLegalContent(legalTexts: LegalTexts) {
      this.legal.imprint = legalTexts.imprint ?? ''
      this.legal.privacyPolicy = legalTexts.privacy ?? ''
      this.legal.terms = legalTexts.termsBody ?? ''
    },

    openLegalModal(content: LegalContentType) {
      this.legal.modalContent = content
      this.legal.modalIsVisible = true
    },

    closeLegalModal() {
      this.legal.modalContent = null
      this.legal.modalIsVisible = false
    },

    async fetchMetaData(uuid: string) {
      const { get } = useApi()
      const config = useRuntimeConfig()

      this.loading = true

      try {
        const data = await get<TransactionMetaDataResponse>(
          `${config.public.transactionMetaData}?uuid=${uuid}`,
        )

        if (data) {
          this.setLogo(data.logo)
          this.setColors(data.paymentData?.colors)
        }
      }
      catch (error) {
        this.error = error instanceof Error ? error.message : 'Unknown error'
      }
      finally {
        this.loading = false
      }
    },

    resetState() {
      this.$reset()
    },
  },
})
